import * as THREE from "three";

/**
 * WindowCycle — 2D canvas procedural generator for the 110s daylight cycle.
 * Renders sky gradients, stars, sun/moon arc with glow, drifting clouds,
 * distant city skyline with window illumination, and animated birds.
 */
export class WindowCycle {
  constructor({ width = 1024, height = 768 } = {}) {
    this.width = width;
    this.height = height;

    this.canvas = document.createElement("canvas");
    this.canvas.width = this.width;
    this.canvas.height = this.height;
    this.ctx = this.canvas.getContext("2d");

    this.texture = new THREE.CanvasTexture(this.canvas);

    // 4 keyframes: dawn, day, dusk, night
    this.palettes = [
      { // dawn
        skyTop: [225, 205, 220], skyMid: [255, 210, 175], skyBot: [255, 175, 155],
        sun: [255, 170, 130], sunAlpha: 0.9,
        buildings: [40, 40, 55], winLit: [200, 255, 100], winProb: 0.15, winColor: [230, 220, 130],
        cloudA: 0.4, foreground: [10, 10, 15], starAlpha: 0,
      },
      { // day
        skyTop: [180, 220, 240], skyMid: [220, 230, 240], skyBot: [235, 235, 232],
        sun: [255, 240, 210], sunAlpha: 0.85,
        buildings: [58, 58, 70], winLit: [255, 255, 220], winProb: 0.08, winColor: [230, 240, 255],
        cloudA: 0.7, foreground: [22, 22, 28], starAlpha: 0,
      },
      { // dusk
        skyTop: [90, 70, 130], skyMid: [230, 130, 110], skyBot: [255, 175, 100],
        sun: [255, 130, 100], sunAlpha: 0.95,
        buildings: [26, 22, 40], winLit: [255, 210, 100], winProb: 0.35, winColor: [255, 200, 90],
        cloudA: 0.5, foreground: [10, 8, 20], starAlpha: 0.15,
      },
      { // night
        skyTop: [8, 10, 30], skyMid: [16, 20, 50], skyBot: [26, 30, 70],
        sun: [230, 230, 255], sunAlpha: 0.75, // moon
        buildings: [10, 12, 22], winLit: [255, 220, 140], winProb: 0.55, winColor: [255, 220, 140],
        cloudA: 0.25, foreground: [4, 4, 10], starAlpha: 0.9,
      },
    ];

    this.buildings = [
      [0.05, 0.55], [0.12, 0.62], [0.19, 0.5], [0.27, 0.65], [0.34, 0.58],
      [0.42, 0.7], [0.5, 0.6], [0.58, 0.68], [0.66, 0.55], [0.74, 0.72],
      [0.82, 0.6], [0.9, 0.66], [0.97, 0.58],
    ];

    this._lastUpdate = 0;
    this._interval = 66; // ~15fps is plenty for an ambient 110s daylight cycle
  }

  update(t) {
    const now = performance.now();
    if (now - this._lastUpdate < this._interval) return;
    this._lastUpdate = now;

    const c = this.ctx;
    const w = this.width;
    const h = this.height;

    // ---- Time-of-day cycle (~110s total) with 4 keyframes ----
    const CYCLE = 110;
    const phase = (((t % CYCLE) + CYCLE) % CYCLE) / CYCLE; // 0..1

    const seg = phase * this.palettes.length;
    const i0 = Math.floor(seg) % this.palettes.length;
    const i1 = (i0 + 1) % this.palettes.length;
    const k = seg - Math.floor(seg);
    const easeK = k * k * (3 - 2 * k); // smoothstep
    const lerp = (a, b) => a + (b - a) * easeK;
    const lerpC = (a, b) => [lerp(a[0], b[0]), lerp(a[1], b[1]), lerp(a[2], b[2])];
    const rgb = (arr) => `rgb(${arr[0] | 0},${arr[1] | 0},${arr[2] | 0})`;
    const rgba = (arr, a) => `rgba(${arr[0] | 0},${arr[1] | 0},${arr[2] | 0},${a})`;

    const P = {
      skyTop: lerpC(this.palettes[i0].skyTop, this.palettes[i1].skyTop),
      skyMid: lerpC(this.palettes[i0].skyMid, this.palettes[i1].skyMid),
      skyBot: lerpC(this.palettes[i0].skyBot, this.palettes[i1].skyBot),
      sun: lerpC(this.palettes[i0].sun, this.palettes[i1].sun),
      sunAlpha: lerp(this.palettes[i0].sunAlpha, this.palettes[i1].sunAlpha),
      buildings: lerpC(this.palettes[i0].buildings, this.palettes[i1].buildings),
      winLit: lerpC(this.palettes[i0].winLit, this.palettes[i1].winLit),
      winProb: lerp(this.palettes[i0].winProb, this.palettes[i1].winProb),
      cloudA: lerp(this.palettes[i0].cloudA, this.palettes[i1].cloudA),
      foreground: lerpC(this.palettes[i0].foreground, this.palettes[i1].foreground),
      starAlpha: lerp(this.palettes[i0].starAlpha, this.palettes[i1].starAlpha),
    };

    // Sky gradient
    const g = c.createLinearGradient(0, 0, 0, h);
    g.addColorStop(0, rgb(P.skyTop));
    g.addColorStop(0.55, rgb(P.skyMid));
    g.addColorStop(1, rgb(P.skyBot));
    c.fillStyle = g;
    c.fillRect(0, 0, w, h);

    // Stars (visible at night/dusk)
    if (P.starAlpha > 0.02) {
      c.fillStyle = `rgba(255,255,255,${P.starAlpha})`;
      for (let s = 0; s < 60; s++) {
        const sx = (s * 173) % w;
        const sy = (s * 89) % (h * 0.55);
        const size = ((s % 3) + 1) * 0.9;
        c.fillRect(sx, sy, size, size);
      }
    }

    // Sun / moon — arcs across the sky based on phase (0..1)
    const arcT = (phase + 0.75) % 1; // shift so sun peaks near "day"
    const sunX = w * (0.15 + arcT * 0.7);
    const sunBaseY = h * (0.7 - Math.sin(arcT * Math.PI) * 0.5);
    c.fillStyle = rgba(P.sun, P.sunAlpha);
    c.beginPath();
    c.arc(sunX, sunBaseY, 55, 0, Math.PI * 2);
    c.fill();

    // Radial glow
    const glow = c.createRadialGradient(sunX, sunBaseY, 20, sunX, sunBaseY, 160);
    glow.addColorStop(0, rgba(P.sun, 0.35 * P.sunAlpha));
    glow.addColorStop(1, rgba(P.sun, 0));
    c.fillStyle = glow;
    c.beginPath();
    c.arc(sunX, sunBaseY, 160, 0, Math.PI * 2);
    c.fill();

    // Slowly drifting clouds
    c.fillStyle = `rgba(255,255,255,${P.cloudA * 0.55})`;
    for (let i = 0; i < 4; i++) {
      const cx = ((i * 300 + t * 8) % (w + 400)) - 200;
      const cy = 80 + i * 30;
      c.beginPath();
      c.ellipse(cx, cy, 90, 22, 0, 0, Math.PI * 2);
      c.fill();
    }

    // Distant city silhouette
    c.fillStyle = rgb(P.buildings);
    this.buildings.forEach(([bx, bh], i) => {
      const x = bx * w - 40;
      const bw = 70 + (i % 3) * 20;
      const bhp = bh * h;
      c.fillStyle = rgb(P.buildings);
      c.fillRect(x, h - bhp, bw, bhp);

      // Windows — probability grows at night
      c.fillStyle = rgba(P.winLit, 0.85);
      for (let y = h - bhp + 20; y < h - 40; y += 22) {
        for (let x2 = x + 10; x2 < x + bw - 10; x2 += 16) {
          const seed = Math.sin(x2 * 12.9898 + y * 78.233 + i) * 43758.5453;
          const r = seed - Math.floor(seed);
          if (r < P.winProb) c.fillRect(x2, y, 5, 8);
        }
      }
    });

    // Foreground silhouette (dark)
    c.fillStyle = rgb(P.foreground);
    c.fillRect(0, h - 60, w, 60);

    // Little flying bird (moves across) — only during day/dusk
    const birdA = 1 - P.starAlpha;
    if (birdA > 0.2) {
      const bx = ((t * 60) % (w + 100)) - 50;
      const by = 150 + Math.sin(t * 2) * 8;
      c.strokeStyle = `rgba(10,10,15,${birdA})`;
      c.lineWidth = 2;
      c.beginPath();
      c.moveTo(bx - 10, by);
      c.quadraticCurveTo(bx - 5, by - 8, bx, by);
      c.quadraticCurveTo(bx + 5, by - 8, bx + 10, by);
      c.stroke();
    }

    this.texture.needsUpdate = true;
  }

  dispose() {
    if (this.texture) {
      this.texture.dispose();
    }
  }
}
